import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { QrService } from '../../services/qr-service.service';
import { LocationService } from '../../services/location.service';

@Component({
  selector: 'app-qr-scanner',
  standalone: true,
  imports: [CommonModule, ZXingScannerModule, FormsModule],
  templateUrl: './qr-scanner.component.html',
  styleUrl: './qr-scanner.component.scss'
})
export class QrScannerComponent implements OnInit, OnDestroy {
  availableDevices: MediaDeviceInfo[] = [];
  currentDevice: MediaDeviceInfo | undefined;
  hasDevices: boolean = false;
  hasPermission: boolean = false;
  qrResultString: string = '';
  qrResult: any;

  allowedFormats = [BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX];

  // UI State
  processing = false;
  menuData: any = null;
  restaurantData: any = null;
  scanError: string | null = null;
  activeTab: 'scan' | 'search' = 'search';

  // Search functionality
  searchQuery: string = '';
  searchResults: any[] = [];
  searching = false;

  constructor(
    private router: Router,
    private qrService: QrService,
    private locationService: LocationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}
  ngOnDestroy(): void {}

  async requestCameraPermission(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.hasPermission = true;
      stream.getTracks().forEach(track => track.stop());
      this.getCameras();
    } catch (error) {
      console.error('Error accessing camera:', error);
      this.hasPermission = false;
    }
  }

  async getCameras(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.availableDevices = devices.filter(device => device.kind === 'videoinput');
      this.hasDevices = this.availableDevices.length > 0;

      if (this.hasDevices) {
        this.currentDevice = this.availableDevices.find(device =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear')
        ) || this.availableDevices[0];
      }
    } catch (error) {
      console.error('Error getting cameras:', error);
      this.hasDevices = false;
    }
  }

  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableDevices = devices;
    this.hasDevices = devices && devices.length > 0;
  }

  onCodeResult(resultString: string): void {
    this.qrResultString = resultString;
    try {
      this.qrResult = JSON.parse(resultString);
      if (this.isBarClikQR(resultString)) {
        this.processBarClikQR(resultString);
      }
    } catch {
      this.qrResult = { text: resultString };
      if (this.isBarClikQR(resultString)) {
        this.processBarClikQR(resultString);
      }
    }
  }

  onHasPermission(hasPermission: boolean): void {
    this.hasPermission = hasPermission;
  }

  onDeviceSelectChange(selectedValue: string | undefined): void {
    if (selectedValue) {
      this.currentDevice = this.availableDevices.find(x => x.deviceId === selectedValue);
    }
  }

  onScanError(error: any): void {
    console.error('Scan error:', error);
  }

  isBarClikQR(qrData: string): boolean {
    return this.qrService.isValidQRFormat(qrData);
  }

  async processBarClikQR(qrData: string): Promise<void> {
    if (this.processing) return;

    this.processing = true;
    this.scanError = null;
    this.menuData = null;
    this.restaurantData = null;

    try {
      const location = await this.locationService.getCurrentPosition().toPromise();
      if (!location) {
        throw new Error('No se pudo obtener la ubicacion. Activa el GPS.');
      }

      const scanResult = await this.qrService.scanQRCode(qrData).toPromise();

      if (scanResult && scanResult.success) {
        this.menuData = scanResult.menu;
        this.restaurantData = scanResult.restaurant;
      } else {
        throw new Error(scanResult?.error || 'Error procesando el codigo QR');
      }
    } catch (error: any) {
      this.scanError = error.message || 'Error desconocido al procesar el QR';
    } finally {
      this.processing = false;
      this.cdr.detectChanges();
    }
  }

  viewMenu(): void {
    if (this.menuData && this.restaurantData) {
      this.router.navigate(['/restaurant-menu'], {
        state: {
          restaurant: this.restaurantData,
          menu: this.menuData,
          source: 'qr-scan'
        }
      });
    }
  }

  clearResults(): void {
    this.qrResultString = '';
    this.qrResult = null;
    this.menuData = null;
    this.restaurantData = null;
    this.scanError = null;
    this.processing = false;
    this.searchResults = [];
    this.searchQuery = '';
    this.searching = false;
  }

  switchTab(tab: 'scan' | 'search'): void {
    this.activeTab = tab;
    this.clearResults();
    if (tab === 'scan') {
      this.requestCameraPermission();
    }
  }

  // Search by name using RxJS subscription (not async/await) for proper change detection
  searchByName(): void {
    const query = this.searchQuery?.trim();
    if (!query || query.length < 2) return;

    this.searching = true;
    this.scanError = null;
    this.searchResults = [];
    this.cdr.detectChanges();

    this.qrService.searchRestaurantsByName(query).subscribe({
      next: (matches) => {
        console.log('Search results:', matches);
        if (!matches || matches.length === 0) {
          this.scanError = `No se encontraron negocios con: "${query}"`;
        } else {
          this.searchResults = matches;
        }
        this.searching = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Search error:', err);
        this.scanError = 'Error en la busqueda. Intenta de nuevo.';
        this.searching = false;
        this.cdr.detectChanges();
      }
    });
  }

  // Navigate to business detail page
  selectRestaurant(restaurant: any): void {
    this.router.navigate(['/business', restaurant.id || 'search'], {
      state: {
        businessData: {
          id: String(restaurant.id || 'search'),
          name: restaurant.name,
          address: '',
          phone: '',
          cuisineType: 'Restaurante',
          priceRange: '',
          rating: 0,
          reviewCount: 0,
          isOpen: true,
          description: '',
          specialOffers: [],
          hours: '',
          menus: restaurant.menus || []
        }
      }
    });
  }

  // Legacy: Load menu directly (used by QR scan result)
  selectMenu(restaurant: any, menu: any): void {
    const apiUrl = menu.api_url;
    if (!apiUrl) {
      this.scanError = 'URL del menu no disponible';
      return;
    }

    this.processing = true;
    this.scanError = null;
    this.cdr.detectChanges();

    this.qrService.getMenuByDirectUrl(apiUrl).subscribe({
      next: (menuData) => {
        if (menuData && menuData.categories && menuData.categories.length > 0) {
          this.processing = false;
          this.searchResults = [];
          this.cdr.detectChanges();

          this.router.navigate(['/restaurant-menu'], {
            state: {
              restaurant: {
                id: restaurant.id,
                name: restaurant.name,
                selectedMenu: menu.menu_name
              },
              menu: menuData,
              source: 'search-selection'
            }
          });
        } else {
          this.processing = false;
          this.scanError = `No se pudo cargar el menu: ${menu.menu_name}`;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.error('Error loading menu:', err);
        this.processing = false;
        this.scanError = `Error cargando menu de ${menu.menu_name}`;
        this.cdr.detectChanges();
      }
    });
  }

  getTotalMenus(): number {
    return this.searchResults.reduce((total, r) => total + (r.menus?.length || 0), 0);
  }
}
